/* Soundscape mixer: layer 1 color-noise source + N nature loops with
 * independent volumes, played through a single AudioContext so levels mix
 * cleanly. The color-noise synthesis itself is delegated to colorNoise.js
 * (we reuse the ScriptProcessorNode-based builder); nature sounds are plain
 * <audio> elements routed through MediaElementAudioSourceNodes.
 *
 * This module intentionally does NOT render UI. Pages drive it via
 * play/stop/setLayerVolume/update. The lifecycle guarantees:
 *   - stop() is always safe to call (idempotent).
 *   - the AudioContext is created on the first user gesture (play).
 *   - nature <audio> tags set crossOrigin='anonymous' so the backend's
 *     wildcard CORS lets us route them through WebAudio for gain control.
 */

const COLOR_SCRIPT_PROCESSOR_SIZE = 4096;

// ---------- Color-noise synthesis (ported from colorNoise.js so we can
// manage multiple contexts without clashing). ----------
function makeNoiseNode(ctx, color) {
    const bufferSize = COLOR_SCRIPT_PROCESSOR_SIZE;
    const node = ctx.createScriptProcessor(bufferSize, 0, 2);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    let brown = 0;
    let lastWhite = 0, lastLastWhite = 0;
    let lp = 0, hp = 0;
    const greyMidGain = 1.6;

    const processors = {
        white: (out) => (out[0] = Math.random() * 2 - 1),
        pink: (out) => {
            const w = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + w * 0.0555179;
            b1 = 0.99332 * b1 + w * 0.0750759;
            b2 = 0.96900 * b2 + w * 0.1538520;
            b3 = 0.86650 * b3 + w * 0.3104856;
            b4 = 0.55000 * b4 + w * 0.5329522;
            b5 = -0.7616 * b5 - w * 0.0168980;
            out[0] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
            b6 = w * 0.115926;
        },
        brown: (out) => {
            const w = Math.random() * 2 - 1;
            brown = (brown + 0.02 * w) / 1.02;
            out[0] = brown * 3.5;
        },
        blue: (out) => {
            const w = Math.random() * 2 - 1;
            out[0] = (w - lastWhite) * 0.5;
            lastWhite = w;
        },
        violet: (out) => {
            const w = Math.random() * 2 - 1;
            out[0] = (w - 2 * lastWhite + lastLastWhite) * 0.25;
            lastLastWhite = lastWhite;
            lastWhite = w;
        },
        grey: (out) => {
            const w = Math.random() * 2 - 1;
            lp += 0.15 * (w - lp);
            hp = w - lp;
            out[0] = (w * 0.6 + hp * greyMidGain * 0.4);
        },
        green: (out) => {
            const w = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + w * 0.0555179;
            b1 = 0.99332 * b1 + w * 0.0750759;
            b2 = 0.96900 * b2 + w * 0.1538520;
            b3 = 0.86650 * b3 + w * 0.3104856;
            b4 = 0.55000 * b4 + w * 0.5329522;
            b5 = -0.7616 * b5 - w * 0.0168980;
            const pink = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
            b6 = w * 0.115926;
            lp += 0.08 * (pink - lp);
            hp = pink - lp;
            out[0] = lp * 1.8 + hp * 0.4;
        },
    };

    const step = processors[color] || processors.white;
    const out = new Float32Array(1);

    node.onaudioprocess = (ev) => {
        const left = ev.outputBuffer.getChannelData(0);
        const right = ev.outputBuffer.getChannelData(1);
        for (let i = 0; i < bufferSize; i++) {
            step(out);
            left[i] = out[0];
            step(out);
            right[i] = out[0];
        }
    };
    return node;
}


// A single layer wrapper. Owns its gain + possibly its own LFO (sleep pulse)
// and either a ScriptProcessorNode (color layer) or an <audio> element
// (nature layer).
class Layer {
    constructor(ctx, spec, resolveNatureUrl) {
        this.ctx = ctx;
        this.spec = { ...spec };
        this.gain = ctx.createGain();
        this.gain.gain.value = clamp01(spec.volume ?? 0.4);
        this.gain.connect(ctx.destination);
        this.noiseNode = null;
        this.audioEl = null;
        this.mediaSource = null;
        this.lfo = null;
        this.lfoGain = null;
        this._resolveNatureUrl = resolveNatureUrl;
    }

    async start() {
        if (this.spec.kind === 'color') {
            this.noiseNode = makeNoiseNode(this.ctx, this.spec.key);
            if (this.spec.sleep) {
                // 1.3 Hz micro-pulses at 65% depth, same shape as the single-
                // noise sleep mode so the two feel identical.
                this.lfo = this.ctx.createOscillator();
                this.lfo.frequency.value = 1.3;
                this.lfoGain = this.ctx.createGain();
                this.lfoGain.gain.value = 0.325;
                const modGain = this.ctx.createGain();
                modGain.gain.value = 0.675;
                this.lfo.connect(this.lfoGain);
                this.lfoGain.connect(modGain.gain);
                this.noiseNode.connect(modGain);
                modGain.connect(this.gain);
                this.lfo.start();
            } else {
                this.noiseNode.connect(this.gain);
            }
            return;
        }
        // Nature layer: resolve the URL from the catalog + route through WebAudio.
        const url = this._resolveNatureUrl(this.spec.key);
        if (!url) return;
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.src = url;
        el.loop = true;
        el.preload = 'auto';
        try {
            this.mediaSource = this.ctx.createMediaElementSource(el);
            this.mediaSource.connect(this.gain);
        } catch (err) {
            // CORS-blocked audio can't go through WebAudio — fall back to
            // the element's own volume so it still plays, just without
            // being summed into the mixer context.
            el.volume = clamp01(this.spec.volume ?? 0.4);
        }
        this.audioEl = el;
        try { await el.play(); } catch { /* user-gesture required, caller handled */ }
    }

    setVolume(v) {
        const vol = clamp01(v);
        this.spec.volume = vol;
        if (this.gain) {
            const t = this.ctx.currentTime;
            this.gain.gain.cancelScheduledValues(t);
            this.gain.gain.linearRampToValueAtTime(vol, t + 0.08);
        }
        if (this.audioEl && !this.mediaSource) {
            // Fallback path when CORS blocked WebAudio routing.
            this.audioEl.volume = vol;
        }
    }

    stop() {
        try { this.noiseNode?.disconnect(); } catch { /* no-op */ }
        try { this.lfo?.stop(); this.lfo?.disconnect(); } catch { /* no-op */ }
        try { this.lfoGain?.disconnect(); } catch { /* no-op */ }
        if (this.audioEl) {
            try { this.audioEl.pause(); } catch { /* no-op */ }
            try { this.mediaSource?.disconnect(); } catch { /* no-op */ }
            this.audioEl.src = '';
            this.audioEl = null;
            this.mediaSource = null;
        }
        try { this.gain?.disconnect(); } catch { /* no-op */ }
        this.noiseNode = null;
        this.lfo = null;
        this.lfoGain = null;
        this.gain = null;
    }
}


function clamp01(v) { return Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0)); }


export class SoundscapeMixer {
    constructor(resolveNatureUrl) {
        this.ctx = null;
        this.layers = [];
        this.playing = false;
        this._resolveNatureUrl = resolveNatureUrl || (() => null);
        // Master output gain — used by the auto-duck mechanism when this mixer
        // is attached to a voice session.
        this.master = null;
        this._baseMasterGain = 1.0;
        this._duckRatio = 1.0;
    }

    _ensureCtx() {
        if (this.ctx) return;
        const AC = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = this._baseMasterGain * this._duckRatio;
        this.master.connect(this.ctx.destination);
    }

    async play(layerSpecs) {
        this._ensureCtx();
        if (this.ctx.state === 'suspended') await this.ctx.resume();
        this.stop({ keepContext: true });

        this.layers = (layerSpecs || []).map((spec) => {
            const l = new Layer(this.ctx, spec, this._resolveNatureUrl);
            // Reroute layer gain through master so auto-duck works.
            try { l.gain.disconnect(); } catch { /* no-op */ }
            l.gain.connect(this.master);
            return l;
        });
        await Promise.all(this.layers.map((l) => l.start()));
        this.playing = this.layers.length > 0;
    }

    stop({ keepContext = false } = {}) {
        this.layers.forEach((l) => l.stop());
        this.layers = [];
        this.playing = false;
        if (!keepContext && this.ctx) {
            try { this.ctx.close(); } catch { /* no-op */ }
            this.ctx = null;
            this.master = null;
        }
    }

    setLayerVolume(index, v) {
        this.layers[index]?.setVolume(v);
    }

    // Set the master gain in two parts: the user's slider ("base") and the
    // auto-duck ratio ("ratio"). Effective = base * ratio.
    setMasterBase(v) {
        this._baseMasterGain = clamp01(v);
        this._applyMaster();
    }

    setDuckRatio(r) {
        this._duckRatio = clamp01(r);
        this._applyMaster();
    }

    _applyMaster() {
        if (!this.master || !this.ctx) return;
        const t = this.ctx.currentTime;
        const eff = clamp01(this._baseMasterGain * this._duckRatio);
        this.master.gain.cancelScheduledValues(t);
        this.master.gain.linearRampToValueAtTime(eff, t + 0.12);
    }
}
